// tour-api.ts — load/save helpers for the virtual-tour graph (CMS 360°
// hotspot-linking flow). Same fetch/CSRF/resp.ok-gate/dual-dispatch contract
// as pano-override.ts's savePanoOverride/savePanoCrop, kept in one place so
// the shape never drifts between the surfaces that read/write the tour
// graph.
//
// baseUrl is the property-scoped prefix ("/organizations/{org}/properties/
// {slug}"), mirroring pano-override.ts's baseUrl convention — every caller
// resolves its own baseUrl and this module only appends "/tour".
import { dispatchToast, getCSRFToken } from "../shared/htmx-helpers";
import { signalPublishStateChanged } from "../shared/publish-state-signal";

export interface TourLink {
  target: string;
  yaw: number;
  pitch: number;
}

export interface TourNode {
  id: string;
  name: Record<string, string>;
  order: number;
  links: TourLink[];
}

export interface TourGraph {
  start_node_id: string;
  nodes: TourNode[];
}

const EMPTY_GRAPH: TourGraph = { start_node_id: "", nodes: [] };

// loadTourGraph GETs the current tour graph for a property. Returns an empty
// graph (never throws/rejects) on a non-2xx response OR a body with no
// `graph` field — the caller (tour editor mount) always has a well-formed
// starting point to render, even for a property that has never saved a tour.
export async function loadTourGraph(baseUrl: string): Promise<TourGraph> {
  const resp = await fetch(`${baseUrl}/tour`, {
    headers: { "X-CSRF-Token": getCSRFToken() },
  });
  if (!resp.ok) return EMPTY_GRAPH;
  const body = await resp.json(); // GET returns { graph }
  return body.graph ?? EMPTY_GRAPH;
}

// saveTourGraph PUTs the full tour graph. The dirty-signal dispatches
// (website-updated / cms-external-dirty / website-content-draft-saved) only
// fire on a confirmed 2xx — same rationale as savePanoOverride: firing them
// unconditionally would tell the publish button / CMS editor / preview a
// save succeeded when a 403, CSRF rejection, or dropped connection actually
// left the graph unchanged.
//
// Returns true on a confirmed save, false on failure (already toasted).
export async function saveTourGraph(
  baseUrl: string,
  graph: TourGraph,
  failMsg?: string,
): Promise<boolean> {
  try {
    const resp = await fetch(`${baseUrl}/tour`, {
      method: "PUT",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ graph }),
    });
    if (!resp.ok) throw new Error(`saveTourGraph ${resp.status}`);
    document.body.dispatchEvent(new CustomEvent("website-updated", { bubbles: true }));
    window.dispatchEvent(new CustomEvent("cms-external-dirty"));
    // The editor's module-scoped listener (editor.ts's _externalDraftSavedBound)
    // reloads the preview iframe on this event; the server re-projects the tour
    // on each preview fetch, so the reload picks up the graph just saved above.
    // Without it, the preview only reflects a tour edit after a manual reload.
    document.body.dispatchEvent(new CustomEvent("website-content-draft-saved", { bubbles: true }));
    // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
    signalPublishStateChanged();
    return true;
  } catch (e) {
    console.error("cms tour graph save failed", e);
    dispatchToast(failMsg || "", "error");
    return false;
  }
}
